import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Picker } from "@design-system/Picker/Picker.lib";
import { ClassCombobox } from "@components/transactions/TransactionSplitV2/ClassCombobox";
import { getClassesAriaLabel } from "@components/transactions/TransactionSplitV2/getClassesAriaLabel";
import { ClassAvatarGroup } from "@components/avatar/ClassAvatarGroup";
import { useClasses } from "@api/class.api";
import { ClassWithChildren } from "@helpers/class";
import { memo, useCallback, useMemo, useState } from "react";

interface ClassAssignment {
    classId: string;
}

interface LineItemWithClasses {
    id: string;
    classAssignments?: ClassAssignment[];
}

interface LineItemClassesCellProps {
    lineItem: LineItemWithClasses;
    onUpdateClasses: (classIds: string[]) => Promise<void>;
    /** Disables the cell (e.g. the invoice/bill's period is locked). */
    readonly?: boolean;
    /** Shown on the trigger when `readonly` is true. */
    tooltip?: string;
}

export const LineItemClassesCell: React.FC<LineItemClassesCellProps> = memo(
    ({ lineItem, onUpdateClasses, readonly, tooltip }) => {
        const { error } = useToaster();
        const [isOpen, setIsOpen] = useState(false);

        const { classesMap, sortedClassesIndexMap } = useClasses();

        const derivedClasses = useMemo(
            () => lineItem.classAssignments?.map((c) => c.classId) ?? [],
            [lineItem.classAssignments],
        );

        const selectedClasses = useMemo(() => {
            if (!derivedClasses.length) {
                return [];
            }
            return derivedClasses
                .map((id) => classesMap.get(id))
                .filter((c): c is ClassWithChildren => Boolean(c))
                .toSorted((a, b) => {
                    const aIdx = sortedClassesIndexMap.get(a.id) ?? Infinity;
                    const bIdx = sortedClassesIndexMap.get(b.id) ?? Infinity;
                    return aIdx - bIdx;
                });
        }, [derivedClasses, classesMap, sortedClassesIndexMap]);

        const handleClassesChange = useCallback(
            async (classIds: string[]) => {
                try {
                    await onUpdateClasses(classIds);
                } catch {
                    error("Failed to update classes");
                }
            },
            [onUpdateClasses, error],
        );

        const triggerContent = useMemo(() => {
            if (selectedClasses.length === 0) {
                return null;
            }
            return <ClassAvatarGroup classes={selectedClasses} max={3} />;
        }, [selectedClasses]);

        const trigger = useMemo(
            () => (
                <Picker.CellContent
                    readOnly={readonly}
                    buttonProps={{ tooltip: readonly ? tooltip : undefined }}
                    showDisabledAdd
                    value={triggerContent ?? undefined}
                    aria-label={getClassesAriaLabel(selectedClasses)}
                />
            ),
            [triggerContent, selectedClasses, readonly, tooltip],
        );

        return (
            <ViewTableCell captureClicks>
                <ClassCombobox
                    selectedClassIds={derivedClasses}
                    onChange={handleClassesChange}
                    disabled={readonly}
                    hideArrow
                    open={isOpen}
                    onOpenChange={setIsOpen}
                    trigger={trigger}
                />
            </ViewTableCell>
        );
    },
);
LineItemClassesCell.displayName = "LineItemClassesCell";
